/**
 * Tool-bridge envelope helpers.
 *
 * The envelope is the *only* thing each platform client (web, desktop, iOS,
 * Android) has to implement, so the shapes live in the contract and these
 * builders exist purely so callers can't forget `version` or misspell `type`.
 * Every builder returns a value that satisfies `envelope.schema.json`.
 *
 * Direction, per the contract:
 *   client → server:  session_init, user_message, tool_result
 *   server → client:  text_delta, tool_call, session_end
 *   either:           error
 */

import type {
  ToolBridgeEnvelope,
  SessionInitEnvelope,
  UserMessageEnvelope,
  ToolResultEnvelope,
  ErrorEnvelope,
  SessionInit,
} from "@qufolio/model";

/** The protocol version this client speaks. */
export const ENVELOPE_VERSION = 1;

/** Frames the client is allowed to send. */
export type OutboundEnvelope =
  | SessionInitEnvelope
  | UserMessageEnvelope
  | ToolResultEnvelope
  | ErrorEnvelope;

/** The tool manifest entry as it travels on the wire (no handler). */
export type ToolManifestEntry = SessionInit["tools"][number];

/** Build the `session_init` frame that registers the client tool manifest. */
export function sessionInitEnvelope(
  tools: ToolManifestEntry[],
  opts: { sessionId?: string; workspaceId?: string; conversationId?: string } = {},
): SessionInitEnvelope {
  return {
    version: ENVELOPE_VERSION,
    type: "session_init",
    ...(opts.sessionId ? { sessionId: opts.sessionId } : {}),
    payload: {
      tools,
      ...(opts.workspaceId ? { workspaceId: opts.workspaceId } : {}),
      // The saved conversation to continue; absent is a new chat.
      ...(opts.conversationId ? { conversationId: opts.conversationId } : {}),
    },
  };
}

/** Build a `user_message` frame carrying one chat turn. */
export function userMessageEnvelope(
  text: string,
  opts: { sessionId?: string; messageId?: string; context?: Record<string, unknown> } = {},
): UserMessageEnvelope {
  return {
    version: ENVELOPE_VERSION,
    type: "user_message",
    ...(opts.sessionId ? { sessionId: opts.sessionId } : {}),
    ...(opts.messageId ? { messageId: opts.messageId } : {}),
    payload: {
      text,
      ...(opts.context ? { context: opts.context } : {}),
    },
  };
}

/** Build a successful `tool_result` frame. */
export function toolResultEnvelope(
  toolCallId: string,
  result: Record<string, unknown> | undefined,
  opts: { sessionId?: string } = {},
): ToolResultEnvelope {
  return {
    version: ENVELOPE_VERSION,
    type: "tool_result",
    ...(opts.sessionId ? { sessionId: opts.sessionId } : {}),
    payload: {
      toolCallId,
      ...(result === undefined ? {} : { result }),
    },
  };
}

/**
 * Build a failed `tool_result` frame.
 *
 * Tool failures travel as results, not as `error` envelopes: the agent is meant
 * to *see* them and self-correct (e.g. re-place an overlapping section), which
 * it can only do if the failure comes back on the tool-call it made.
 */
export function toolErrorEnvelope(
  toolCallId: string,
  errorMessage: string,
  opts: { sessionId?: string } = {},
): ToolResultEnvelope {
  return {
    version: ENVELOPE_VERSION,
    type: "tool_result",
    ...(opts.sessionId ? { sessionId: opts.sessionId } : {}),
    payload: { toolCallId, isError: true, errorMessage },
  };
}

/** Narrow an already-validated envelope by its `type` discriminator. */
export function isEnvelopeOfType<T extends ToolBridgeEnvelope["type"]>(
  envelope: ToolBridgeEnvelope,
  type: T,
): envelope is Extract<ToolBridgeEnvelope, { type: T }> {
  return envelope.type === type;
}
